package com.milta.web

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import com.milta.core.localization.LocalStrings
import com.milta.feature.admin.domain.entity.AdminAccount
import com.milta.feature.admin.domain.entity.AdminOrganisation
import com.milta.feature.admin.domain.usecase.GetAdminAccountsUseCase
import com.milta.feature.admin.presentation.AdminUsersIntent
import com.milta.feature.admin.presentation.AdminUsersState
import com.milta.feature.admin.presentation.AdminUsersViewModel
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.fontSize
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Text
import org.koin.compose.koinInject

// Admin-only neutral tone, not part of the shared semantic palette (Web Success/Warning/Error) —
// a team member's own Subscription rarely gates anything real (see pillFor's doc), so it gets a
// deliberately muted pill rather than borrowing a color that implies it matters.
private const val AdminNeutral = WebTextSecondary
private const val AdminNeutralBg = WebSurfaceDim

/** Column widths shared by the header bar and every row (org, member, independent) so content
 * lines up — mirrors the validated mockup exactly. */
private const val AdminGridTemplate = "2.1fr 1.6fr 0.95fr 1.1fr 1.35fr 1fr 1.15fr 0.9fr"

/**
 * Admin "Utilisateurs" screen (2026-10-03) — read-only, no create/update/delete intent exists on
 * purpose (see AdminUsersViewModel doc). Organisations collapsed by default; independent drivers
 * are plain rows in the same list, not a second visually separate table (explicit feedback during
 * mockup review — see docs/admin-role-dashboard-requirements.md in the milta repo).
 */
@Composable
fun AdminUsersContent(
    /** MIL-97 — opens the "Fiche utilisateur" (A2) of this account id. */
    onOpenAccount: (accountId: String) -> Unit = {},
    /** MIL-97 — hoisted by the Admin branch so the search and the expanded organisations survive a
     * round trip to the "Fiche utilisateur"; created locally otherwise. */
    hoistedViewModel: AdminUsersViewModel? = null,
) {
    val getAdminAccounts: GetAdminAccountsUseCase = koinInject()
    val viewModel = hoistedViewModel ?: remember { AdminUsersViewModel(getAdminAccounts) }
    val state by viewModel.state.collectAsState()

    LaunchedEffect(Unit) { viewModel.onIntent(AdminUsersIntent.Load) }

    val strings = LocalStrings.current.adminUsers

    when {
        state.isLoading -> Div(attrs = { style { padding(40.px); property("display", "flex"); property("align-items", "center") } }) {
            WebSpinner()
            Div(attrs = { style { padding(0.px, 0.px, 0.px, 12.px); property("color", WebTextSecondary); fontSize(13.5.px) } }) { Text(strings.loading) }
        }
        state.error != null -> Div(
            attrs = { style { padding(16.px); property("background", WebErrorBg); property("color", WebErrorInk); property("border-radius", "10px") } },
        ) { Text(if (state.error == "offline") strings.errorNoConnection else strings.errorGeneric) }
        else -> AdminUsersBody(state = state, onIntent = viewModel::onIntent, onOpenAccount = onOpenAccount)
    }
}

@Composable
private fun AdminUsersBody(state: AdminUsersState, onIntent: (AdminUsersIntent) -> Unit, onOpenAccount: (String) -> Unit) {
    val strings = LocalStrings.current.adminUsers
    val data = state.data
    val organisations = data?.organisations.orEmpty()
    val independents = data?.independentDrivers.orEmpty()

    val totalAccounts = organisations.sumOf { 1 + it.members.size } + independents.size
    val totalPatrons = organisations.size
    val totalDrivers = totalAccounts - totalPatrons
    val allAccounts = organisations.flatMap { listOf(it.patron) + it.members } + independents
    val activeSubs = allAccounts.count { it.subscription?.status in setOf("Active", "GracePeriod") }
    val atRisk = allAccounts.count { it.subscription?.status in setOf("Trial", "Expired") }

    Div(attrs = {
        style {
            property("display", "flex"); property("align-items", "center"); property("gap", "8px")
            property("background", WebSurface); property("border", "1px solid $WebBorder")
            property("border-radius", "10px"); padding(9.px, 14.px); property("margin-bottom", "18px")
            fontSize(12.5.px); property("color", WebTextSecondary)
        }
    }) { Text(strings.readOnlyNotice) }

    Div(attrs = { style { property("display", "grid"); property("grid-template-columns", "repeat(5, 1fr)"); property("gap", "12px"); property("margin-bottom", "20px") } }) {
        Kpi(strings.kpiAccountsLabel, totalAccounts.toString(), strings.kpiAccountsSubtitle)
        Kpi(strings.kpiDriversLabel, totalDrivers.toString(), strings.kpiDriversSubtitle)
        Kpi(strings.kpiPatronsLabel, totalPatrons.toString(), "$totalPatrons ${if (totalPatrons == 1) strings.kpiPatronsOrgSingular else strings.kpiPatronsOrgPlural}")
        Kpi(strings.kpiActiveSubsLabel, activeSubs.toString(), strings.kpiActiveSubsSubtitle)
        Kpi(strings.kpiAtRiskLabel, atRisk.toString(), strings.kpiAtRiskSubtitle)
    }

    Div(attrs = { style { property("display", "flex"); property("align-items", "center"); property("gap", "10px"); property("margin-bottom", "16px"); property("flex-wrap", "wrap") } }) {
        Div(attrs = {
            style {
                property("flex", "1"); property("min-width", "220px"); property("display", "flex"); property("align-items", "center"); property("gap", "8px")
                property("background", WebSurface); property("border", "1px solid $WebBorder"); property("border-radius", "10px"); padding(9.px, 12.px)
            }
        }) {
            Text("🔎")
            Input(type = InputType.Text, attrs = {
                attr("placeholder", strings.searchPlaceholder)
                style { property("border", "none"); property("outline", "none"); property("background", "transparent"); fontSize(13.5.px); width(100.percent); property("color", WebTextPrimary) }
                value(state.searchQuery)
                onInput { event -> onIntent(AdminUsersIntent.UpdateSearch(event.value)) }
            })
        }
        ToolbarButton(strings.expandAllAction) { onIntent(AdminUsersIntent.ExpandAll) }
        ToolbarButton(strings.collapseAllAction) { onIntent(AdminUsersIntent.CollapseAll) }
    }

    Div(attrs = {
        style {
            property("background", WebSurface); property("border", "1px solid $WebBorder"); property("border-radius", "10px")
            property("box-shadow", "0 1px 2px rgba(30,58,74,0.06)"); property("margin-bottom", "14px")
        }
    }) {
        GridRow(padding = "10px 16px") {
            HeaderCell(strings.columnAccount); HeaderCell(strings.columnContact); HeaderCell(strings.columnType)
            HeaderCell(strings.columnAddedDate); HeaderCell(strings.columnSubscription); HeaderCell(strings.columnSince); HeaderCell(strings.columnRenewal, alignRight = true)
            // The "Détails" column has no header (mockup B2).
            HeaderCell("", alignRight = true)
        }
    }

    val query = state.searchQuery.trim().lowercase()

    Div(attrs = {
        style {
            property("background", WebSurface); property("border", "1px solid $WebBorder"); property("border-radius", "14px")
            property("box-shadow", "0 1px 2px rgba(30,58,74,0.06)"); property("overflow", "hidden")
        }
    }) {
        val visibleOrgs = organisations.filter { org -> query.isEmpty() || orgMatches(org, query) }
        visibleOrgs.forEachIndexed { index, org ->
            val orgNameMatches = query.isNotEmpty() && (org.name.lowercase().contains(query) || patronMatches(org.patron, query))
            val hasMatchingMember = query.isNotEmpty() && org.members.any { accountMatches(it, query) }
            val isExpanded = org.enterpriseId in state.expandedOrganisationIds || (query.isNotEmpty() && !orgNameMatches && hasMatchingMember)
            val isLast = index == visibleOrgs.lastIndex && independents.isEmpty()
            OrganisationRow(
                org = org,
                expanded = isExpanded,
                query = query,
                isLast = isLast,
                onToggle = { onIntent(AdminUsersIntent.ToggleOrganisation(org.enterpriseId)) },
                onOpenAccount = onOpenAccount,
            )
        }

        val visibleIndependents = independents.filter { query.isEmpty() || accountMatches(it, query) }
        if (visibleIndependents.isNotEmpty()) {
            Div(attrs = {
                style {
                    padding(9.px, 16.px); fontSize(10.5.px); property("font-weight", "700"); property("color", WebTextSecondary)
                    property("text-transform", "uppercase"); property("letter-spacing", "0.05em")
                    property("background", WebSurfaceDim); property("border-bottom", "1px solid $WebBorder")
                    property("border-top", if (visibleOrgs.isNotEmpty()) "1px solid $WebBorder" else "none")
                }
            }) { Text(strings.noOrganizationHeader) }

            visibleIndependents.forEachIndexed { index, account ->
                IndependentRow(account = account, isLast = index == visibleIndependents.lastIndex, onOpen = { onOpenAccount(account.accountId) })
            }
        }

        if (visibleOrgs.isEmpty() && visibleIndependents.isEmpty()) {
            Div(attrs = { style { padding(36.px, 16.px); property("text-align", "center"); property("color", WebTextSecondary); fontSize(13.px) } }) {
                Text(strings.emptySearchResult)
            }
        }
    }
}

private fun orgMatches(org: AdminOrganisation, query: String): Boolean =
    org.name.lowercase().contains(query) ||
        patronMatches(org.patron, query) ||
        org.members.any { accountMatches(it, query) }

private fun patronMatches(patron: AdminAccount, query: String) = accountMatches(patron, query)

private fun accountMatches(account: AdminAccount, query: String): Boolean =
    "${account.firstName} ${account.lastName}".lowercase().contains(query) ||
        (account.email?.lowercase()?.contains(query) == true) ||
        account.phoneNumber.lowercase().contains(query)

@Composable
private fun OrganisationRow(
    org: AdminOrganisation,
    expanded: Boolean,
    query: String,
    isLast: Boolean,
    onToggle: () -> Unit,
    onOpenAccount: (String) -> Unit,
) {
    val strings = LocalStrings.current.adminUsers
    Div(attrs = { style { if (!isLast) property("border-bottom", "1px solid $WebBorder") } }) {
        Div(attrs = {
            onClick { onToggle() }
            style { property("cursor", "pointer") }
        }) {
            GridRow(padding = "13px 16px") {
                Div(attrs = { style { property("display", "flex"); property("align-items", "center"); property("gap", "10px"); property("min-width", "0") } }) {
                    Div(attrs = { style { width(14.px); property("display", "inline-block"); property("color", WebTextSecondary); property("transition", "transform 0.15s ease"); if (expanded) property("transform", "rotate(90deg)") } }) { Text("▶") }
                    AvatarAndName(org.patron, org.name)
                }
                Cell(contactOf(org.patron))
                Div { Badge(label = "${strings.patronBadgePrefix} + ${org.members.size} 👤", enterprise = true) }
                Cell("${strings.createdOnPrefix} ${formatDate(org.createdAt)}")
                SubscriptionCell(org.patron)
                Cell(formatDate(org.patron.subscription?.since))
                RenewalsCell(org.patron.subscription?.renewalCount ?: 0)
                // The row click still expands the team; "Détails" opens the Enterprise account's details.
                DetailsButtonCell(onClick = { onOpenAccount(org.patron.accountId) })
            }
        }
        if (expanded) {
            Div(attrs = { style { property("background", WebSurfaceDim); property("border-top", "1px solid $WebBorder") } }) {
                if (org.members.isEmpty()) {
                    Div(attrs = { style { padding(10.px, 16.px); fontSize(13.px); property("color", WebTextSecondary) } }) {
                        Text(strings.emptyTeamMessage)
                    }
                } else {
                    org.members.forEachIndexed { index, member ->
                        Div(attrs = {
                            onClick { onOpenAccount(member.accountId) }
                            style {
                                property("cursor", "pointer")
                                if (index != org.members.lastIndex) property("border-bottom", "1px solid $WebBorder")
                            }
                        }) {
                            GridRow(padding = "9px 16px") {
                                Div(attrs = { style { property("display", "flex"); property("align-items", "center"); property("gap", "10px"); property("padding-left", "22px"); property("position", "relative"); property("min-width", "0") } }) {
                                    AvatarAndName(member, null)
                                }
                                Cell(contactOf(member))
                                Div { Badge(label = strings.driverBadge, enterprise = false) }
                                Cell("${strings.addedOnPrefix} ${formatDate(member.joinedTeamAt)}")
                                Div { Pill(label = strings.viaOrganization, fg = AdminNeutral, bg = AdminNeutralBg) }
                                Cell(formatDate(member.subscription?.since))
                                RenewalsCell(member.subscription?.renewalCount ?: 0)
                                DetailsButtonCell(onClick = { onOpenAccount(member.accountId) })
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun IndependentRow(account: AdminAccount, isLast: Boolean, onOpen: () -> Unit) {
    val strings = LocalStrings.current.adminUsers
    Div(attrs = {
        onClick { onOpen() }
        style {
            property("cursor", "pointer")
            if (!isLast) property("border-bottom", "1px solid $WebBorder")
        }
    }) {
        GridRow(padding = "11px 16px") {
            Div(attrs = { style { property("display", "flex"); property("align-items", "center"); property("gap", "10px"); property("padding-left", "22px"); property("min-width", "0") } }) {
                AvatarAndName(account, null)
            }
            Cell(contactOf(account))
            Div { Badge(label = strings.driverBadge, enterprise = false) }
            Cell(strings.accountCreatedOnUnknown)
            SubscriptionCell(account)
            Cell(formatDate(account.subscription?.since))
            RenewalsCell(account.subscription?.renewalCount ?: 0)
            DetailsButtonCell(onClick = onOpen)
        }
    }
}

/** Last column, same on every row (mockup B2): the "Détails" button opens the account's details
 * without triggering the row's own click (which expands an Enterprise's team). */
@Composable
private fun DetailsButtonCell(onClick: () -> Unit) {
    Div(attrs = { style { property("text-align", "right") } }) {
        Button(attrs = {
            onClick { event ->
                event.stopPropagation()
                onClick()
            }
            style {
                secondaryButtonStyle(this)
                fontSize(12.px); property("font-weight", "700"); property("padding", "5px 12px"); property("white-space", "nowrap")
            }
        }) { Text(LocalStrings.current.optimizationQuota.detailsAction) }
    }
}

@Composable
private fun GridRow(padding: String, content: @Composable () -> Unit) {
    Div(attrs = {
        style {
            property("display", "grid"); property("grid-template-columns", AdminGridTemplate)
            property("align-items", "center"); property("gap", "8px"); property("padding", padding)
        }
    }) { content() }
}

@Composable
private fun HeaderCell(label: String, alignRight: Boolean = false) {
    Div(attrs = { style { fontSize(10.5.px); property("font-weight", "700"); property("color", WebTextSecondary); property("text-transform", "uppercase"); property("letter-spacing", "0.05em"); if (alignRight) property("text-align", "right") } }) {
        Text(label)
    }
}

@Composable
private fun Cell(text: String) {
    Div(attrs = { style { fontSize(12.5.px); property("color", WebTextSecondary) } }) { Text(text) }
}

@Composable
private fun RenewalsCell(count: Int) {
    val strings = LocalStrings.current.adminUsers
    Div(attrs = { style { fontSize(12.5.px); property("color", WebTextSecondary); property("text-align", "right") } }) {
        Div(attrs = { style { property("display", "inline"); property("color", WebTextPrimary); property("font-weight", "700") } }) { Text("${count}×") }
        Text(" ${if (count == 1) strings.renewalSingular else strings.renewalPlural}")
    }
}

@Composable
private fun AvatarAndName(account: AdminAccount, overrideName: String?) {
    Div(attrs = {
        style {
            width(32.px); height(32.px); property("border-radius", "50%")
            property("background", avatarColorFor(initials("${account.firstName} ${account.lastName}")))
            property("display", "flex"); property("align-items", "center"); property("justify-content", "center")
            property("color", "#FFFFFF"); property("font-weight", "700"); fontSize(12.5.px); property("flex-shrink", "0")
        }
    }) { Text(initials("${account.firstName} ${account.lastName}")) }
    Div(attrs = { style { property("font-weight", "600"); property("color", WebTextPrimary); property("white-space", "nowrap"); property("overflow", "hidden"); property("text-overflow", "ellipsis") } }) {
        Text(overrideName ?: "${account.firstName} ${account.lastName}")
    }
}

private fun contactOf(account: AdminAccount) = account.email ?: account.phoneNumber

@Composable
private fun SubscriptionCell(account: AdminAccount) {
    val strings = LocalStrings.current.adminUsers
    val sub = account.subscription
    if (sub == null) {
        Div { Pill(label = strings.subscriptionNone, fg = AdminNeutral, bg = AdminNeutralBg) }
        return
    }
    val (label, fg, bg) = when (sub.status) {
        "Active" -> Triple(strings.subscriptionActive, WebSuccessInk, WebSuccessBg)
        "Trial" -> Triple(strings.subscriptionTrial, WebWarningInk(), WebWarningBg)
        "GracePeriod" -> Triple(strings.subscriptionGrace, WebWarningInk(), WebWarningBg)
        "Expired" -> Triple(strings.subscriptionExpiredStatus, WebErrorInk, WebErrorBg)
        "Cancelled" -> Triple(strings.subscriptionCancelled, WebErrorInk, WebErrorBg)
        else -> Triple(sub.status, AdminNeutral, AdminNeutralBg)
    }
    Div {
        Pill(label = label, fg = fg, bg = bg)
        Div(attrs = { style { fontSize(10.5.px); property("color", WebTextSecondary); property("margin-top", "2px") } }) {
            Text(if (sub.plan == "Organisation") strings.planOrganization else strings.planDriver)
        }
    }
}

// WebWarning is the raw accent (used for e.g. icon tint elsewhere) — no pre-declared "ink" shade
// exists for it like WebSuccessInk/WebErrorInk, so this derives one consistent with that pair's
// darker-for-text convention rather than reusing the lighter accent directly on a light pill background.
private fun WebWarningInk() = "#8A5A0A"

@Composable
private fun Badge(label: String, enterprise: Boolean) {
    Div(attrs = {
        style {
            property("display", "inline-flex"); property("align-items", "center"); fontSize(11.px); property("font-weight", "700")
            padding(3.px, 8.px); property("border-radius", "999px"); property("white-space", "nowrap")
            if (enterprise) { property("color", WebPrimary); property("background", WebActiveTint) }
            else { property("color", WebPrimaryMid); property("background", "#E8F2F5") }
        }
    }) { Text(label) }
}

@Composable
private fun Pill(label: String, fg: String, bg: String) {
    Div(attrs = {
        style {
            property("display", "inline-flex"); property("align-items", "center"); property("gap", "6px")
            fontSize(11.px); property("font-weight", "700"); padding(3.px, 9.px); property("border-radius", "999px")
            property("color", fg); property("background", bg); property("white-space", "nowrap")
        }
    }) {
        Div(attrs = { style { width(6.px); height(6.px); property("border-radius", "50%"); property("background", fg) } })
        Text(label)
    }
}

/** Reusable stat-card — also used by [AdminOcrAnalysisContent]. */
@Composable
internal fun Kpi(label: String, value: String, sub: String) {
    Div(attrs = {
        style {
            property("background", WebSurface); property("border", "1px solid $WebBorder"); property("border-radius", "12px")
            padding(14.px, 16.px); property("box-shadow", "0 1px 2px rgba(30,58,74,0.06)")
        }
    }) {
        Div(attrs = { style { fontSize(11.px); property("color", WebTextSecondary); property("text-transform", "uppercase"); property("letter-spacing", "0.05em"); property("font-weight", "700") } }) { Text(label) }
        Div(attrs = { style { fontSize(23.px); property("font-weight", "700"); property("margin-top", "4px"); property("color", WebTextPrimary) } }) { Text(value) }
        Div(attrs = { style { fontSize(11.5.px); property("color", WebTextSecondary); property("margin-top", "2px") } }) { Text(sub) }
    }
}

@Composable
private fun ToolbarButton(label: String, onClick: () -> Unit) {
    Div(attrs = {
        onClick { onClick() }
        style {
            fontSize(12.5.px); property("font-weight", "600"); property("color", WebTextSecondary)
            property("background", WebSurface); property("border", "1px solid $WebBorder"); property("border-radius", "999px")
            padding(7.px, 13.px); property("cursor", "pointer")
        }
    }) { Text(label) }
}

/** "2026-06-12T08:34:38+00:00" / "2026-06-12" -> "12/06/2026". Same string-split approach as
 * WebHistoriqueScreen.formatDateDdMmYyyy — no kotlinx-datetime dependency needed for this. */
private fun formatDate(iso: String?): String {
    if (iso.isNullOrBlank()) return "—"
    val datePart = iso.substringBefore('T')
    val parts = datePart.split("-")
    return if (parts.size == 3) "${parts[2]}/${parts[1]}/${parts[0]}" else iso
}
